import React, { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { toast } from 'react-toastify';
import config from '../../config';
import authService from '../../utils/authService';

const AdminSettingsGuard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = authService.getUser();
      const username = user?.email || user?.username;

      if (!username) {
        toast.error('User data not found. Please log in again.');
        setLoading(false);
        return;
      }

      const response = await fetch(`${config.API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': 'https://staging.filterhotels.com',
          'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS, HEAD',
          'Access-Control-Allow-Credentials': true
        },
        credentials: 'include',
        body: JSON.stringify({
          username,
          password
        })
      });

      if (response.status === 200) {
        const data = await response.json();
        if (data.success) {
          setIsAuthenticated(true);
          toast.success('Access granted to Settings');
        } else {
          toast.error(data.message || 'Incorrect password');
        }
      } else if (response.status === 401) {
        toast.error('Incorrect password. Please try again.');
      } else {
        toast.error('Authentication failed. Please try again.');
      }
    } catch (error) {
      console.error('Error verifying password:', error);
      toast.error('Network error. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (isAuthenticated) {
    return <Outlet />;
  }

  return (
    <div className="flex items-center justify-center min-h-[60vh] bg-gray-50">
      <div className="max-w-md w-full bg-white p-8 rounded-lg shadow-md border border-gray-200">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-lock text-2xl"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Security Verification</h2>
          <p className="text-gray-600 mt-2">Please enter your password to access admin settings.</p>
        </div>

        <form onSubmit={handleVerify} className="space-y-6">
          <div>
            <label htmlFor="guard-password" className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="guard-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-gray-400 hover:text-gray-600`}></i>
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || !password}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Verifying...' : 'Verify Access'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminSettingsGuard;
